import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../api/auth';

/**
 * Redeems the `?token=` from a magic-link email at `/auth/verify`. Ported from
 * grant_management. The `useRef` guard matters: React 18 StrictMode fires effects
 * twice in dev, and a one-time token must be redeemed only once.
 */
const MagicLinkVerify: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthData } = useAuth();
  const [error, setError] = useState('');
  const called = useRef(false);

  useEffect(() => {
    if (called.current) return;
    called.current = true;

    const token = searchParams.get('token');
    if (!token) {
      setError('No token found in the link. Please request a new one.');
      return;
    }

    authApi
      .verifyMagicLink(token)
      .then((data) => {
        setAuthData(data.user, data.token);
        const destination = data.next || '/settings/donations';
        navigate(destination, { replace: true });
      })
      .catch((err) => {
        setError(
          err.response?.data?.error || 'This link is invalid or has expired.',
        );
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full text-center px-4">
        {error ? (
          <>
            <p className="text-red-600 mb-4" data-testid="magic-link-error-message">
              {error}
            </p>
            <Link
              to="/login"
              className="text-indigo-600 hover:underline text-sm"
              data-testid="magic-link-back-to-login-link"
            >
              Back to sign in
            </Link>
          </>
        ) : (
          <p className="text-gray-500 text-sm" data-testid="magic-link-loading-message">
            Signing you in…
          </p>
        )}
      </div>
    </div>
  );
};

export default MagicLinkVerify;
